// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

// Plugins
import {
  convertLength,
  formatNumber,
} from '../../../../../../plugins/general';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import {
  LengthType,
  MeasurementType,
} from '../../../../../../types/global';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import {
  Title,
  TitleSize,
  TitleWeight,
} from '../../../../../../layout/elements/Title';

// View
import {
  CSSettingsContainer,
  CSSettingsContent,
  ParagraphUnitContainer,
} from './CSSettings.style';
import { CSSettingsProps } from './CSSettings.types';

function CSSettings({
  className,
  testId,
  unitData,
}: CSSettingsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();
  const userLengthUnit: LengthType = getMeasurementUnit(MeasurementType.length);

  const paragraphSize: ParagraphSize = isXs ? ParagraphSize.sm : ParagraphSize.md;
  const csSettingValue: string = formatNumber(
    convertLength(unitData?.csSetting, userLengthUnit).value || 0,
    2,
  );
  const hasCSSetting: boolean = !_.isNil(unitData?.csSetting);

  const renderValue = (): JSX.Element => {
    if (isXs) {
      return (
        <Paragraph
          testId={`${testId}-value`}
          weight={ParagraphWeight.medium}
        >
          {csSettingValue}
        </Paragraph>
      );
    }
    return (
      <Title
        testId={`${testId}-value`}
        size={TitleSize.xxs}
        weight={TitleWeight.medium}
      >
        {csSettingValue}
      </Title>
    );
  };

  const renderUnit = (): JSX.Element | null => {
    if (!hasCSSetting) {
      return null;
    }

    if (userLengthUnit === LengthType.inch) {
      if (isXs) {
        return (
          <ParagraphUnitContainer>
            <Paragraph weight={ParagraphWeight.bold}>
              {userLengthUnit}
            </Paragraph>
          </ParagraphUnitContainer>
        );
      }
      return (
        <ParagraphUnitContainer>
          <Title
            size={TitleSize.xxs}
            weight={TitleWeight.bold}
          >
            {userLengthUnit}
          </Title>
        </ParagraphUnitContainer>
      );
    }

    return (
      <ParagraphUnitContainer>
        <Paragraph
          weight={ParagraphWeight.bold}
          size={paragraphSize}
        >
          {userLengthUnit}
        </Paragraph>
      </ParagraphUnitContainer>
    );
  };

  return (
    <CSSettingsContainer data-testid={testId} className={className}>
      <Paragraph
        testId={`${testId}-title`}
        weight={ParagraphWeight.bold}
        size={paragraphSize}
      >
        {t('view.crusher.csSetting')}
      </Paragraph>
      <CSSettingsContent data-testid={`${testId}-content`}>
        {renderValue()}
        {renderUnit()}
      </CSSettingsContent>
    </CSSettingsContainer>
  );
}

export { CSSettings };
